import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Snackbar,
  Alert,
  LinearProgress,
  useTheme,
  alpha,
  styled
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { getAdminDashboard, getWorkflowMappingSummary } from "../../api/apiService";

export default function AdminDashboard() {
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [workflowStats, setWorkflowStats] = useState({});
  const theme = useTheme();
  
  const StyledCard = styled(Card)(({ theme, bgcolor }) => ({
    borderRadius: 24,
    overflow: 'hidden',
    position: 'relative',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[8],
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: bgcolor || theme.palette.primary.main,
    }
  }));
  
  const MetricIconWrapper = styled(Box)(({ theme, bgcolor }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: alpha(bgcolor || theme.palette.primary.main, 0.1),
    color: bgcolor || theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    '& svg': {
      fontSize: 28,
    }
  }));

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [dashboardRes, workflowRes] = await Promise.all([
          getAdminDashboard(),
          getWorkflowMappingSummary()
        ]);
        setStats(dashboardRes.data || {});
        setWorkflowStats(workflowRes.data || {});
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
        setSnackbar({ 
          open: true, 
          message: "Failed to load dashboard data: " + (e.message || 'Unknown error'), 
          severity: "error" 
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const metricCards = useMemo(() => ([
    { 
      label: "Total Users", 
      value: stats?.totalUsers ?? 0, 
      icon: <PeopleIcon />, 
      color: theme.palette.primary.main,
    },
    { 
      label: "Total Clients", 
      value: stats?.totalClients ?? 0, 
      icon: <BusinessIcon />, 
      color: theme.palette.success.main,
    },
    { 
      label: "Total Campaigns", 
      value: stats?.totalCampaigns ?? 0, 
      icon: <CampaignIcon />, 
      color: theme.palette.warning.main,
    },
    { 
      label: "Total Leads", 
      value: stats?.totalLeads ?? 0, 
      icon: <PersonAddIcon />, 
      color: theme.palette.info.main,
    },
    { 
      label: "Total Emails", 
      value: stats?.totalEmails ?? 0, 
      icon: <EmailIcon />, 
      color: theme.palette.error.main,
    },
    { 
      label: "Active Subscriptions", 
      value: stats?.activeSubscriptions ?? 0, 
      icon: <CardMembershipIcon />, 
      color: theme.palette.secondary.main,
    },
  ]), [stats, theme.palette]);

  const systemHealth = stats?.systemHealth || { status: "unknown", issues: [] };

  return (
    <Box sx={{ mb: 2 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Admin Dashboard</Typography>
      </Box>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>

        {/* Metrics Cards */}
        {metricCards.map((metric, idx) => (
          <Grid key={idx} size={{xs:6, sm:6, md:4}} lg={2}>
            <StyledCard 
              elevation={0} 
              bgcolor={metric.color}
              sx={{ 
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.paper, 0.8) 
                  : '#fff',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <CardContent>
                <Box sx={{py:2}} display="flex" justifyContent="space-between" alignItems="flex-start">
                  <div>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography 
                      variant="h4" 
                      fontWeight={800}
                      sx={{ 
                        fontSize: '2rem',
                        background: `linear-gradient(45deg, ${metric.color}, ${alpha(metric.color, 0.8)})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: 1.2,
                        mb: 0
                      }}
                    >
                      {metric.value.toLocaleString()}
                    </Typography>
                    {/* {metric.trend !== undefined && (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        sx={{ 
                          color: metric.trend >= 0 ? 'success.main' : 'error.main',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        {metric.trend >= 0 ? '↑' : '↓'} {Math.abs(metric.trend)}%
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          vs last month
                        </Typography>
                      </Box>
                    )} */}
                  </div>
                  <MetricIconWrapper sx={{mt:1}} bgcolor={metric.color}>
                    {metric.icon}
                  </MetricIconWrapper>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}

        {/* Workflow Mapping Statistics - full width */}
        <Grid size={{ xs: 12 }} sx={{ mt: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
            <CardContent sx={{pb:0}}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* <Box component="span" sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: 'success.main',
                  display: 'inline-block'
                }} /> */}
                Workflow Mapping Statistics
              </Typography>
              
              <Grid container spacing={2} sx={{ mx:1 }}>
                <Grid size={{ xs: 12, sm:6, md:3 }} >
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Total Workflows</Typography>
                    <Box display="flex" alignItems="flex-end" gap={1}>
                      <Typography variant="h4" fontWeight={700}>
                        {workflowStats.totalWorkflows ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        ({workflowStats.activeWorkflows ?? 0} active)
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                
                <Grid size={{ xs: 12, sm:6, md:3 }}>
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Workflow Status</Typography>
                    <Box sx={{py:2}} display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Active</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {workflowStats.activeWorkflows ?? 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Inactive</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {workflowStats.inactiveWorkflows ?? 0}
                        </Typography>
                      </Box>
                      {/* <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Completed</Typography>
                        <Typography variant="body2" color="warning.main">
                          {workflowStats.completedWorkflows ?? 0}
                        </Typography>
                      </Box> */}
                    </Box>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm:6, md:3 }}>
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Executions</Typography>
                    <Box sx={{py:2}} display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Total</Typography>
                        <Typography variant="body2">{workflowStats.totalExecutions ?? 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Successful</Typography>
                        <Typography variant="body2" color="success.main">
                          {workflowStats.totalSuccessfulExecutions ?? 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Failed</Typography>
                        <Typography variant="body2" color="error.main">
                          {workflowStats.totalFailedExecutions ?? 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm:6, md:3 }}>
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Success Rate
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `conic-gradient(
                              #4caf50 0% ${(workflowStats.averageSuccessRate || 0) * 100}%, 
                              #f5f5f5 ${(workflowStats.averageSuccessRate || 0) * 100}% 100%
                            )`
                          }}
                        >
                          <Box
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: '50%',
                              bgcolor: 'background.paper',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6" fontWeight={700}>
                              {((workflowStats.averageSuccessRate || 0) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Average Success Rate
                        </Typography>
                        <Typography variant="body2">
                          Based on {workflowStats.totalExecutions ?? 0} executions
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              {/* {workflowStats.workflowsByType && Object.keys(workflowStats.workflowsByType).length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Workflows by Type
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {Object.entries(workflowStats.workflowsByType).map(([type, count]) => (
                      <Box 
                        key={type}
                        sx={{
                          px: 0.6,
                          py: 0.5,
                          borderRadius: 4,
                          bgcolor: 'action.hover',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          border: 1,
                          borderColor: theme.palette.divider
                        }}
                      >
                        <Typography variant="caption" textTransform="capitalize">
                          {type.replace(/_/g, ' ')}
                        </Typography>
                        <Box 
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        >
                          {count}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )} */}
            </CardContent>
          </Card>
        </Grid>

        {/* System Health - full width */}
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>System Health</Typography>
              <Typography variant="body1" sx={{ mb: 1, color:systemHealth?.status === "healthy"? theme.palette.success.main : "gray" }}>
                <strong>{String(systemHealth?.status || "unknown").toUpperCase()}</strong>
              </Typography>
              {Array.isArray(systemHealth?.issues) && systemHealth.issues.length > 0 ? (
                <Box>
                  {systemHealth.issues.map((issue, i) => (
                    <Typography key={i} variant="body2">• {issue}</Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No issues reported.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
